import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader, DatePicker } from "rsuite";
import frFR from "rsuite/locales/fr_FR";
import { FiSearch } from "react-icons/fi";
import { GiSettingsKnobs } from "react-icons/gi";
import { GoChevronDown, GoChevronUp } from "react-icons/go";
import { MdFirstPage, MdLastPage, MdNavigateNext, MdNavigateBefore } from "react-icons/md";

import API from "../../utils/api";
import PrescriberLayout from "../../components/PrescriberLayout";
import Select from "../../components/Select";
import { useConfig } from "../../context/config-context";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * DocumentsListPage - Page publique listant les nouveaux documents après validation OTP
 *
 * Route: /prescriber-access/:permanentToken/documents
 */
export default function DocumentsListPage() {
  const { permanentToken } = useParams();
  const navigate = useNavigate();
  const siteConfig = useConfig();

  const primaryColor = siteConfig.cssVariables?.["--primary"] || "#24335d";
  //const accentColor = siteConfig.cssVariables?.["--dark"] || "#eb477d";
  const ecosystem = siteConfig?.medias?.logo?.path?.split("/")?.[1] || "diabete";
  const svgSupported = ecosystem === "diabete";
  const crIcons = {
    eye: `/${ecosystem}/icons/cr/eye.${svgSupported ? "svg" : "png"}`,
    download: `/${ecosystem}/icons/cr/download.${svgSupported ? "svg" : "png"}`,
    sortDown: `/${ecosystem}/icons/cr/sort-down.${svgSupported ? "svg" : "png"}`,
    chevronDown: `/${ecosystem}/icons/cr/chevron-down.png`,
    chevronUp: `/${ecosystem}/icons/cr/chevron-up.png`,
  };

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);

  // Filtres
  const [patientSearch, setPatientSearch] = useState("");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Tri
  const [sortField, setSortField] = useState("patientName");
  const [sortOrder, setSortOrder] = useState("asc");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Lignes dépliées
  const [expandedRows, setExpandedRows] = useState({});

  // Erreur action (view/download)
  const [actionError, setActionError] = useState(null);
  const errorTimerRef = useRef(null);

  // Document en cours de chargement (id du doc)
  const [loadingDoc, setLoadingDoc] = useState(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const sessionToken = sessionStorage.getItem("prescriberSessionToken");

      const response = await API.get(
        `/prescriber-access/${permanentToken}/documents`,
        {
          headers: { Authorization: `Bearer ${sessionToken}` },
          silent: true,
        }
      );

      if (response.data.success) {
        setDocuments(response.data.data || []);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("prescriberSessionToken");
        sessionStorage.removeItem("permanentToken");
        navigate(`/prescriber-access/${permanentToken}`);
      }
    } finally {
      setLoading(false);
    }
  }, [permanentToken, navigate]);

  const verifySession = useCallback(async () => {
    const sessionToken = sessionStorage.getItem("prescriberSessionToken");
    const storedToken = sessionStorage.getItem("permanentToken");

    if (!sessionToken || storedToken !== permanentToken) {
      navigate(`/prescriber-access/${permanentToken}`);
      return;
    }
    await fetchDocuments();
  }, [permanentToken, navigate, fetchDocuments]);

  const applyFilters = useCallback(() => {
    let filtered = [...documents];

    if (patientSearch.trim()) {
      const search = patientSearch.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.patientName || "").toLowerCase().includes(search)
      );
    }

    if (documentTypeFilter) {
      filtered = filtered.filter((item) =>
        (item.documents || []).some((doc) => doc.type === documentTypeFilter)
      );
    }

    if (dateFilter) {
      filtered = filtered.filter((item) => {
        if (!item.interventionDate) return false;
        const d = new Date(item.interventionDate);
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${mo}-${day}` === dateFilter;
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let aVal = sortField === "patientName" ? (a.patientName || "") : (a.interventionDate || "");
      let bVal = sortField === "patientName" ? (b.patientName || "") : (b.interventionDate || "");
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredDocuments(filtered);
    setCurrentPage(1);
  }, [documents, patientSearch, documentTypeFilter, dateFilter, sortField, sortOrder]);

  useEffect(() => { verifySession(); }, [verifySession]);
  useEffect(() => { applyFilters(); }, [applyFilters]);

  const resetFilters = () => {
    setPatientSearch("");
    setDocumentTypeFilter("");
    setDateFilter("");
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const ACTION_ERRORS = {
    FILE_NOT_FOUND: "Ce document n'est pas disponible sur le serveur. Il est possible qu'il n'ait pas encore été synchronisé.",
    ACCESS_DENIED: "Vous n'êtes pas autorisé à consulter ce document.",
    SESSION_NOT_FOUND: "Votre session a expiré. Veuillez vous reconnecter.",
    SESSION_EXPIRED: "Votre session a expiré. Veuillez vous reconnecter.",
  };

  const showActionError = (message) => {
    setActionError(message);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => setActionError(null), 10000);
  };

  const fetchDocument = async (documentId, inline) => {
    setActionError(null);
    setLoadingDoc(inline ? `view-${documentId}` : `download-${documentId}`);
    const sessionToken = sessionStorage.getItem("prescriberSessionToken");
    const apiBase = siteConfig.api?.baseUrl || "";
    const endpoint = inline ? "view" : "download";
    const url = `${apiBase}/prescriber-access/${permanentToken}/${endpoint}/${documentId}?sessionToken=${sessionToken}${inline ? "" : "&download=true"}`;

    try {
      const res = await fetch(url);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const code = data?.error?.code || "FILE_NOT_FOUND";
        showActionError(ACTION_ERRORS[code] || "Une erreur est survenue lors du chargement du document.");
        return;
      }
      const contentType = res.headers.get("content-type") || "";
      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
      const filename = filenameMatch?.[1] || `document-${documentId}`;

      if (inline && contentType.includes("application/pdf")) {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch {
      showActionError("Une erreur est survenue lors du chargement du document.");
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleViewDocument = (documentId) => fetchDocument(documentId, true);
  const handleDownloadDocument = (documentId) => fetchDocument(documentId, false);

  // Pagination
  const totalRows = filteredDocuments.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const paginatedRows = filteredDocuments.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalRows);

  // Types de documents uniques
  const documentTypes = [
    ...new Set(
      documents.flatMap((item) =>
        (item.documents || []).map((doc) => doc.type).filter(Boolean)
      )
    ),
  ];

  const prescriberPortalUrl =
    siteConfig.url || "https://prescripteurs.isisdiabete.fr";

  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const isAsc = isActive && sortOrder === "asc";
    return (
      <img
        src={crIcons.sortDown}
        alt="tri"
        className="ml-1 flex-shrink-0"
        style={{
          width: "7px",
          height: "7px",
          opacity: isActive ? 1 : 0.35,
          transform: isAsc ? "rotate(180deg)" : "none",
        }}
      />
    );
  };

  // Sidebar
  const sidebar = (
    <aside className="w-full md:w-[285px] flex-shrink-0 flex flex-col gap-4">
      {/* Filtrer */}
      <div className="rounded-lg overflow-hidden shadow-md">
        <div
          className="flex items-center gap-2 px-3 py-2 text-lg font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          <GiSettingsKnobs size={14} className="rotate-90" />
          <span>Filtrer</span>
        </div>
        <div className="p-4 flex flex-col gap-4 bg-white pt-6">
          {/* Patient */}
          <div>
            <label className="text-xs font-semibold text-neutral-dark block mb-1">
              Patient
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un patient"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                className="w-full pl-3 pr-7 border border-gray-300 rounded text-sm outline-none focus:border-primary transition-colors font-normal"
                style={{ height: "30px" }}
              />
              <FiSearch
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                size={13}
              />
            </div>
          </div>

          {/* Type de document */}
          <div>
            <label className="text-xs font-semibold text-neutral-dark block mb-1">
              Type de document
            </label>
            <Select
              key={documentTypeFilter}
              value={documentTypeFilter}
              onChange={(val) => setDocumentTypeFilter(val)}
              size="sm"
              data={[
                { value: "", label: "Choisir un type" },
                ...documentTypes.map((type) => ({ value: type, label: type })),
              ]}
            />
          </div>

          {/* Date du RDV */}
          <div>
            <label className="text-xs font-semibold text-neutral-dark block mb-1">
              Date du RDV
            </label>
            <DatePicker
              value={dateFilter ? new Date(dateFilter) : null}
              onChange={(date) => {
                if (!date) { setDateFilter(""); return; }
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                const d = String(date.getDate()).padStart(2, "0");
                setDateFilter(`${y}-${m}-${d}`);
              }}
              onClean={() => setDateFilter("")}
              locale={frFR.DatePicker}
              format="dd/MM/yyyy"
              placeholder="JJ/MM/AAAA"
              oneTap
              block
              size="sm"
            />
          </div>

          {/* Réinitialiser */}
          <button
            onClick={resetFilters}
            className="w-full py-1.5 font-semibold text-white rounded transition-colors mt-2 hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Pour aller plus loin */}
      <div className="shadow-md rounded-lg p-4 bg-white">
        <h3 className="text-lg font-semibold mb-2 text-neutral-dark">
          Pour aller plus loin
        </h3>
        <p className="text-sm text-neutral-dark mb-4">
          Pour accéder à l'intégralité des informations des patients de votre
          file active, pensez à vous connecter sur{" "}
          <a
            href={prescriberPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-70 text-neutral-dark"
          >
            {prescriberPortalUrl.replace(/^https?:\/\//, "")}
          </a>
          {"."}
        </p>
        <a
          href={`${prescriberPortalUrl}/login`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-1.5 text-sm text-center hover:text-primary font-semibold text-primary border border-primary rounded transition-colors hover:opacity-90 block"
        >
          Se connecter
        </a>
      </div>

      {/* Logo preset */}
      <div className="hidden md:block mt-2">
        <img
          src={siteConfig.medias.logo_preset.path}
          alt={siteConfig.medias.logo_preset.alt}
          className="w-36 object-contain"
        />
      </div>
    </aside>
  );

  return (
    <PrescriberLayout wide>
      {actionError && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          {actionError}
        </div>
      )}
      <h1 className="text-2xl font-semibold mb-6" style={{ color: primaryColor }}>
        Nouveaux documents de votre file active
      </h1>
      <div className="flex flex-col md:flex-row gap-5">
        {/* Sidebar */}
        {sidebar}

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          {/* Tableau */}
          <div className="rounded-lg overflow-hidden">
            {/* Header colonnes — fond blanc, texte #495056 */}
            <div
              className="grid items-center text-xs font-semibold bg-white"
              style={{
                gridTemplateColumns: "1fr 1fr 1fr 1fr 84px",
                borderLeft: `3px solid transparent`,
                paddingLeft: "28px",
                height: "45px",
                color: "#495056",
              }}
            >
              <button
                className="flex items-center text-left hover:opacity-70"
                style={{ color: "#495056" }}
                onClick={() => toggleSort("patientName")}
              >
                Patient <SortIcon field="patientName" />
              </button>
              <span>Nombre de documents</span>
              <button
                className="flex items-center hover:opacity-70"
                style={{ color: "#495056" }}
                onClick={() => toggleSort("interventionDate")}
              >
                Date du RDV <SortIcon field="interventionDate" />
              </button>
              <span>Type de RDV</span>
              <span />
            </div>

            {/* Corps */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader size="md" />
              </div>
            ) : paginatedRows.length === 0 ? (
              <p className="text-center text-sm text-gray-500 py-12">
                Aucun nouveau document disponible
              </p>
            ) : (
              paginatedRows.map((row, idx) => {
                const rowKey = row._id || idx;
                const isExpanded = !!expandedRows[rowKey];
                const rowBg = idx % 2 === 0 ? "#F6F9FF" : "#ffffff";
                return (
                  <div key={rowKey} className="relative">
                    {/* Border left avec espacement */}
                    <div
                      style={{
                        position: "absolute",
                        left: "8px",
                        top: "8px",
                        bottom: "8px",
                        width: "5px",
                        backgroundColor: "#EEB9D1", // Accent color clair
                        borderRadius: "9999px",
                      }}
                    />
                    {/* Ligne principale */}
                    <div
                      className="grid items-center text-sm cursor-pointer"
                      style={{
                        gridTemplateColumns: "1fr 1fr 1fr 1fr 84px",
                        paddingLeft: "28px",
                        minHeight: "70px",
                        backgroundColor: rowBg,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F2F3F5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = rowBg;
                      }}
                      onClick={() => toggleRow(rowKey)}
                    >
                      <span
                        className="font-semibold truncate"
                        style={{ color: primaryColor }}
                      >
                        {row.patientName || "-"}
                      </span>
                      <span className="text-neutral-dark">
                        {row.documentCount ?? (row.documents || []).length}
                      </span>
                      <span className="text-neutral-dark">
                        {row.interventionDate
                          ? new Date(row.interventionDate).toLocaleDateString(
                              "fr-FR",
                            )
                          : "-"}
                      </span>
                      <span className="text-neutral-dark">
                        {row.interventionType || "FTC"}
                      </span>
                      <div
                        className="flex justify-end"
                        style={{ marginRight: "24px" }}
                      >
                        <button
                          className="w-7 h-7 rounded-[5px] flex items-center justify-center border-2 border-[#CEDEEB] text-dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(rowKey);
                          }}
                        >
                          {isExpanded ? (
                            <GoChevronUp
                              className="text-neutral-dark"
                              alt="Replier"
                              size={20}
                            />
                          ) : (
                            <GoChevronDown
                              className="text-neutral-dark"
                              alt="Déplier"
                              size={20}
                            />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Sous-lignes documents */}
                    {isExpanded &&
                      (row.documents || []).map((doc, dIdx) => (
                        <div
                          key={doc.id || dIdx}
                          style={{
                            minHeight: "70px",
                            backgroundColor: rowBg,
                          }}
                        >
                          <div
                            style={{
                              margin: "0 24px",
                              height: "1px",
                              backgroundColor: "#e5e7eb",
                            }}
                          />
                          <div
                            className="grid items-center text-sm gap-0"
                            style={{
                              gridTemplateColumns:
                                "1fr 1fr 1fr 1fr 84px",
                              paddingLeft: "28px",
                              paddingRight: "0px",
                              paddingTop: "18px",
                              paddingBottom: "16px",
                            }}
                          >
                            <div>
                              <p className="text-xs font-semibold text-neutral-medium">
                                Type de document
                              </p>
                              <span className="text-sm font-normal text-neutral-dark">
                                {doc.type || "-"}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-neutral-medium">
                                Nom du document
                              </p>
                              <span className="text-sm font-normal text-neutral-dark">
                                {doc.name || "-"}
                              </span>
                            </div>
                            <div />
                            <div />
                            <div
                              className="flex items-center justify-end gap-2"
                              style={{ marginRight: "24px" }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewDocument(doc.id);
                                }}
                                disabled={!!loadingDoc}
                                className="flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                                style={{ backgroundColor: primaryColor, width: 33, height: 33, borderRadius: 3, flexShrink: 0 }}
                                title="Visualiser"
                              >
                                {loadingDoc === `view-${doc.id}` ? (
                                  <Loader size="xs" inverse />
                                ) : (
                                  <img
                                    src={crIcons.eye}
                                    alt="Visualiser"
                                    style={{ width: 24, height: 24 }}
                                    className="object-contain"
                                  />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadDocument(doc.id);
                                }}
                                disabled={!!loadingDoc}
                                className="flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed p-1"
                                style={{ backgroundColor: primaryColor, width: 33, height: 33, borderRadius: 3, flexShrink: 0 }}
                                title="Télécharger"
                              >
                                {loadingDoc === `download-${doc.id}` ? (
                                  <Loader size="xs" inverse />
                                ) : (
                                  <img
                                    src={crIcons.download}
                                    alt="Télécharger"
                                    style={{ width: 25, height: 25 }}
                                    className="object-contain"
                                  />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                );
              })
            )}

            {/* Pagination */}
            {!loading && totalRows > 0 && (
              <div className="flex items-center justify-end gap-4 px-4 py-6 text-sm text-neutral-medium">
                <div className="flex items-center gap-2">
                  <span>Patient par page :</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="text-sm bg-transparent"
                  >
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <span className="mr-2">
                    {rangeStart}–{rangeEnd} sur {totalRows}
                  </span>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <MdFirstPage size={18} />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <MdNavigateBefore size={18} />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <MdNavigateNext size={18} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <MdLastPage size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PrescriberLayout>
  );
}
